import apiClient from "./apiClient";
import AppError from "../utils/AppError";
import {
  showErrorNotification,
  showSuccessNotification,
  showInfoNotification,
} from "../utils/notifications";
import Export from "../models/Export";

const ExportService = {
  async getExports({ page = 1, page_size = 25 } = {}) {
    try {
      const params = { page, page_size };
      const { data } = await apiClient.get("/exports", { params });
      return {
        ...data,
        results: data.results.map(Export.fromServerModel),
      };
    } catch (error) {
      const appError = new AppError({
        title: "Failed to Fetch Exports",
        response: error.response,
        userMessage: "Could not retrieve the exports.",
      });
      showErrorNotification(appError);
      throw appError;
    }
  },

  async patchExport(exportId, payload) {
    try {
      const exportPayload = new Export(payload).toServerModel();
      const editableFields = ["status", "isHidden"];
      const reducedPayload = Object.keys(exportPayload).reduce((acc, key) => {
        if (editableFields.includes(key)) {
          acc[key] = exportPayload[key];
        }
        return acc;
      }, {});

      const { data } = await apiClient.patch(
        `/exports/${exportId}`,
        reducedPayload
      );
      return Export.fromServerModel(data);
    } catch (error) {
      console.error("Error updating export:", error);
      const appError = new AppError({
        title: "Failed to Update Export",
        response: error.response,
        userMessage: "Could not update the export.",
      });
      showErrorNotification(appError);
      throw appError;
    }
  },

  async getExportOutputs(exportId) {
    try {
      const { data } = await apiClient.get(`/exports/${exportId}/outputs`);
      return data;
    } catch (error) {
      const appError = new AppError({
        title: "Failed to Fetch Export Outputs",
        response: error.response,
        userMessage: "Could not retrieve the export outputs.",
      });
      showErrorNotification(appError);
      throw appError;
    }
  },

  async createExport(payload) {
    try {
      const response = await apiClient.post("/exports", { payload });
      const { data } = response;
      const exportObj = Export.fromServerModel(data);

      if (response.status === 208) {
        showInfoNotification(
          "Export Already Exists",
          data.msg || "This export already exists and is ready to download."
        );
        exportObj.isDuplicate = true;
      } else {
        showSuccessNotification(
          "Export Started",
          `Export "${
            data.file_display_names?.[0] || data.id
          }" started successfully.`
        );
      }

      return exportObj;
    } catch (error) {
      console.error("Error creating export:", error);
      const appError = new AppError({
        title: "Failed to Start Export",
        response: error.response,
        userMessage: "Could not start the export.",
      });
      if (error.response.status !== 412) {
        showErrorNotification(appError);
      }
      throw appError;
    }
  },
};

export default ExportService;
